/* eslint-disable no-nested-ternary */
import React, { forwardRef, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Slide,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
} from '@mui/material';
import Iconify from '../common/iconify.component';
import Loading from '../table-elements/loading.component';
import { getChannelRulesetList } from '../../services/apps.service';
import { validateChannelSchedule } from '../../services/ruleset.service';

const Transition = forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} {...props} />
));

const ValidateSetup = ({ onCancel }) => {
  const dataChannel = useRef([]);
  const channelDetails = useRef({
    total: 0,
    processed: 0,
    valid: 0,
    invalid: 0,
  });
  const scheduleDates = [
    '2025-06-01',
    '2025-06-08',
    '2025-06-15',
    '2025-06-22',
    '2025-06-29',
    '2025-07-06',
    '2025-07-13',
    '2025-07-20',
    '2025-07-27',
    '2025-08-03',
    '2025-08-10',
    '2025-08-17',
    '2025-08-24',
    '2025-08-31',
  ];

  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [processingChannel, setProcessingChannel] = useState(null);

  const handleProcessChannels = async () => {
    setProcessing(true);

    const channelsList = [...dataChannel.current];
    dataChannel.current = [];

    channelsList.forEach((c) => {
      dataChannel.current.push({
        id: c.id,
        channel: c.channel,
        processed: false,
        schedulingRules: c.schedulingRules || 0,
        assignmentRules: c.assignmentRules || 0,
      });
    });

    channelDetails.current = {
      total: dataChannel.current.length,
      processed: 0,
      valid: 0,
      invalid: 0,
    };

    for (let i = 0; i < dataChannel.current.length; i += 1) {
      const channel = dataChannel.current[i];
      setProcessingChannel(channel.id);

      const response = await validateChannelSchedule(channel.id, scheduleDates);

      dataChannel.current[i] = {
        ...channel,
        processed: true,
        ...(response?.data || {}),
      };

      channelDetails.current.processed += 1;
      if (response?.data?.valid) {
        channelDetails.current.valid += 1;
      } else {
        channelDetails.current.invalid += 1;
      }
    }

    setProcessingChannel(null);
    setProcessing(false);
  };

  useEffect(() => {
    getChannelRulesetList()
      .then((res) => {
        const channelsList = res?.data || [];
        dataChannel.current = [];

        channelsList.forEach((c) => {
          dataChannel.current.push({
            id: c.id,
            channel: `${c.code} - ${c.title}`,
            processed: false,
            schedulingRules: c.schedulingRules || 0,
            assignmentRules: c.assignmentRules || 0,
          });
        });

        setLoading(false);
      })
      .catch(() => {
        onCancel();
      });
  }, []);

  if (loading) {
    return <Loading />;
  }

  return (
    <Dialog
      open
      aria-labelledby="add-dialog-title"
      aria-describedby="add-dialog-description"
      TransitionComponent={Transition}
      fullWidth
      maxWidth="lg"
    >
      <DialogTitle id="add-dialog-title">Validate Channels Setup</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onCancel}
        size="small"
        sx={{
          position: 'absolute',
          right: 10,
          top: 15,
          color: (theme) => theme.palette.grey[500],
        }}
        disabled={loading}
      >
        <Iconify icon="ic:outline-close" />
      </IconButton>

      <DialogContent dividers id="add-dialog-description">
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Typography variant="body2" color="red">
            {channelDetails.current.invalid > 0 && (
              <>
                Total {channelDetails.current.invalid} channel(s) are invalid.
              </>
            )}
          </Typography>

          <Button
            color="inherit"
            variant="contained"
            onClick={() => handleProcessChannels()}
            disabled={processing}
          >
            {processing
              ? `Processing ${channelDetails.current.processed} of ${channelDetails.current.total} Channels`
              : 'Validate Setup'}
          </Button>
        </Box>

        <TableContainer
          component={Paper}
          variant="outlined"
          square
          sx={{ overflowX: 'auto', height: 600, overflowY: 'auto' }}
        >
          <Table
            size="small"
            aria-label="a dense table"
            stickyHeader
            sx={{
              minWidth: 700,
              tableLayout: 'fixed',
            }}
          >
            <TableHead sx={{ position: 'sticky', top: 0, zIndex: 3 }}>
              <TableRow>
                <TableCell
                  width={250}
                  sx={{
                    position: 'sticky',
                    left: 0,
                    zIndex: 2,
                    py: 0,
                    fontSize: '0.7rem',
                    lineHeight: '1.2rem',
                    borderRight: '1px solid rgba(224, 224, 224, 1)',
                  }}
                  rowSpan={3}
                >
                  Channel Name
                </TableCell>
                <TableCell
                  width={100}
                  sx={{ fontSize: '0.7rem', lineHeight: '1.2rem', zIndex: 1 }}
                  align="center"
                  rowSpan={3}
                >
                  Assignment Rulesets
                </TableCell>
                <TableCell
                  width={100}
                  sx={{
                    fontSize: '0.7rem',
                    lineHeight: '1.2rem',
                    zIndex: 1,
                    borderRight: '1px solid rgba(224, 224, 224, 1)',
                  }}
                  align="center"
                  rowSpan={3}
                >
                  Scheduling Rulesets
                </TableCell>
                <TableCell
                  width={100}
                  sx={{
                    fontSize: '0.7rem',
                    lineHeight: '1.2rem',
                    zIndex: 1,
                  }}
                  align="center"
                  rowSpan={3}
                >
                  Assigned Videos
                </TableCell>
                <TableCell
                  width={100}
                  sx={{ fontSize: '0.7rem', lineHeight: '1.2rem', zIndex: 1 }}
                  align="center"
                  rowSpan={3}
                >
                  Assigned Movies
                </TableCell>
                <TableCell
                  width={100}
                  sx={{ fontSize: '0.7rem', lineHeight: '1.2rem', zIndex: 1 }}
                  align="center"
                  rowSpan={3}
                >
                  Assigned Episodes
                </TableCell>
                <TableCell
                  width={100}
                  sx={{ fontSize: '0.7rem', lineHeight: '1.2rem', zIndex: 1 }}
                  align="center"
                  rowSpan={3}
                >
                  Assigned Series
                </TableCell>
                <TableCell
                  width={100}
                  sx={{
                    fontSize: '0.7rem',
                    lineHeight: '1.2rem',
                    zIndex: 1,
                    borderRight: '1px solid rgba(224, 224, 224, 1)',
                  }}
                  align="center"
                  rowSpan={3}
                >
                  Assigned Seasons
                </TableCell>
                <TableCell
                  width={100}
                  sx={{ fontSize: '0.7rem', lineHeight: '1.2rem', zIndex: 1 }}
                  align="center"
                  rowSpan={3}
                >
                  Always Scheduled Movies
                </TableCell>
                <TableCell
                  width={100}
                  sx={{
                    fontSize: '0.7rem',
                    lineHeight: '1.2rem',
                    zIndex: 1,
                    borderRight: '1px solid rgba(224, 224, 224, 1)',
                  }}
                  align="center"
                  rowSpan={3}
                >
                  Always Scheduled Episodes
                </TableCell>
                <TableCell
                  width={100}
                  sx={{ fontSize: '0.7rem', lineHeight: '1.2rem', zIndex: 1 }}
                  align="center"
                  rowSpan={3}
                >
                  Blocked Always Scheduled Movies
                </TableCell>
                <TableCell
                  width={100}
                  sx={{
                    fontSize: '0.7rem',
                    lineHeight: '1.2rem',
                    zIndex: 1,
                    borderRight: '1px solid rgba(224, 224, 224, 1)',
                  }}
                  align="center"
                  rowSpan={3}
                >
                  Blocked Always Scheduled Episodes
                </TableCell>
                <TableCell
                  width={100}
                  sx={{ fontSize: '0.7rem', lineHeight: '1.2rem', zIndex: 1 }}
                  align="center"
                  rowSpan={3}
                >
                  Blocked Movies
                </TableCell>
                <TableCell
                  width={100}
                  sx={{
                    fontSize: '0.7rem',
                    lineHeight: '1.2rem',
                    zIndex: 1,
                    borderRight: '1px solid rgba(224, 224, 224, 1)',
                  }}
                  align="center"
                  rowSpan={3}
                >
                  Blocked Episodes
                </TableCell>
                <TableCell
                  width={100}
                  sx={{ fontSize: '0.7rem', lineHeight: '1.2rem', zIndex: 1 }}
                  align="center"
                  rowSpan={3}
                >
                  Target Movies
                </TableCell>
                <TableCell
                  width={100}
                  sx={{
                    fontSize: '0.7rem',
                    lineHeight: '1.2rem',
                    zIndex: 1,
                    borderRight: '1px solid rgba(224, 224, 224, 1)',
                  }}
                  align="center"
                  rowSpan={3}
                >
                  Target Episodes
                </TableCell>
                <TableCell
                  width={100}
                  sx={{ fontSize: '0.7rem', lineHeight: '1.2rem', zIndex: 1 }}
                  align="center"
                  rowSpan={3}
                >
                  Weekly Schedule
                </TableCell>
                {scheduleDates.map((date) => (
                  <TableCell
                    key={`head-${date}`}
                    width={300}
                    sx={{
                      fontSize: '0.7rem',
                      lineHeight: '1.2rem',
                      zIndex: 1,
                      borderLeft: '1px solid rgba(224, 224, 224, 1)',
                    }}
                    align="center"
                    colSpan={5}
                  >
                    {date}
                  </TableCell>
                ))}
              </TableRow>
              <TableRow>
                {scheduleDates.map((date) => (
                  <>
                    <TableCell
                      key={`head-live-${date}`}
                      width={180}
                      sx={{
                        fontSize: '0.6rem',
                        lineHeight: '1.2rem',
                        zIndex: 1,
                        borderLeft: '1px solid rgba(224, 224, 224, 1)',
                      }}
                      align="center"
                      colSpan={3}
                    >
                      Live
                    </TableCell>
                    <TableCell
                      key={`head-sd-${date}`}
                      width={120}
                      sx={{
                        fontSize: '0.6rem',
                        lineHeight: '1.2rem',
                        zIndex: 1,
                      }}
                      align="center"
                      colSpan={2}
                    >
                      + / -
                    </TableCell>
                  </>
                ))}
              </TableRow>
              <TableRow>
                {scheduleDates.map((date) => (
                  <>
                    <TableCell
                      key={`head-t-${date}`}
                      width={60}
                      sx={{
                        fontSize: '0.6rem',
                        lineHeight: '1.2rem',
                        zIndex: 1,
                        borderLeft: '1px solid rgba(224, 224, 224, 1)',
                      }}
                      align="center"
                    >
                      Total
                    </TableCell>
                    <TableCell
                      key={`head-m-${date}`}
                      width={60}
                      sx={{
                        fontSize: '0.6rem',
                        lineHeight: '1.2rem',
                        zIndex: 1,
                      }}
                      align="center"
                    >
                      Movies
                    </TableCell>
                    <TableCell
                      key={`head-e-${date}`}
                      width={60}
                      sx={{
                        fontSize: '0.6rem',
                        lineHeight: '1.2rem',
                        zIndex: 1,
                      }}
                      align="center"
                    >
                      Episodes
                    </TableCell>
                    <TableCell
                      key={`head-s-${date}`}
                      width={60}
                      sx={{
                        fontSize: '0.6rem',
                        lineHeight: '1.2rem',
                        zIndex: 1,
                      }}
                      align="center"
                    >
                      Sche.
                    </TableCell>
                    <TableCell
                      key={`head-d-${date}`}
                      width={60}
                      sx={{
                        fontSize: '0.6rem',
                        lineHeight: '1.2rem',
                        zIndex: 1,
                      }}
                      align="center"
                    >
                      De-sche.
                    </TableCell>
                  </>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {dataChannel.current.map((channel, idx) => (
                <TableRow
                  key={`channel-${channel.id}-${idx}`}
                  sx={{
                    backgroundColor: channel.processed
                      ? channel.valid
                        ? '#edffeb'
                        : '#ffe9e8'
                      : '#ffffff',
                  }}
                >
                  <TableCell
                    width={250}
                    sx={{
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      fontSize: '0.7rem',
                      lineHeight: '1.2rem',
                      backgroundColor: 'inherit',
                      borderRight: '1px solid rgba(224, 224, 224, 1)',
                    }}
                  >
                    {channel.channel}
                  </TableCell>
                  <TableCell
                    width={100}
                    sx={{ fontSize: '0.7rem', lineHeight: '1.2rem', zIndex: 1 }}
                    align="center"
                  >
                    {channel.assignmentRules || 0}
                  </TableCell>
                  <TableCell
                    width={100}
                    sx={{
                      fontSize: '0.7rem',
                      lineHeight: '1.2rem',
                      zIndex: 1,
                      borderRight: '1px solid rgba(224, 224, 224, 1)',
                    }}
                    align="center"
                  >
                    {channel.schedulingRules || 0}
                  </TableCell>
                  <TableCell
                    width={100}
                    sx={{
                      fontSize: '0.7rem',
                      lineHeight: '1.2rem',
                      zIndex: 1,
                      filter: `blur(${channel.processed ? '0' : '3'}px)`,
                    }}
                    align="center"
                  >
                    {channel.totalVideos || 0}
                  </TableCell>
                  <TableCell
                    width={100}
                    sx={{
                      fontSize: '0.7rem',
                      lineHeight: '1.2rem',
                      zIndex: 1,
                      filter: `blur(${channel.processed ? '0' : '3'}px)`,
                    }}
                    align="center"
                  >
                    {channel.targetMovies || 0}
                  </TableCell>
                  <TableCell
                    width={100}
                    sx={{
                      fontSize: '0.7rem',
                      lineHeight: '1.2rem',
                      zIndex: 1,
                      filter: `blur(${channel.processed ? '0' : '3'}px)`,
                    }}
                    align="center"
                  >
                    {channel.totalEpisodes || 0}
                  </TableCell>
                  <TableCell
                    width={100}
                    sx={{
                      fontSize: '0.7rem',
                      lineHeight: '1.2rem',
                      zIndex: 1,
                      filter: `blur(${channel.processed ? '0' : '3'}px)`,
                    }}
                    align="center"
                  >
                    {channel.totalSeries || 0}
                  </TableCell>
                  <TableCell
                    width={100}
                    sx={{
                      fontSize: '0.7rem',
                      lineHeight: '1.2rem',
                      zIndex: 1,
                      borderRight: '1px solid rgba(224, 224, 224, 1)',
                      filter: `blur(${channel.processed ? '0' : '3'}px)`,
                    }}
                    align="center"
                  >
                    {channel.totalSeasons || 0}
                  </TableCell>
                  <TableCell
                    width={100}
                    sx={{
                      fontSize: '0.7rem',
                      lineHeight: '1.2rem',
                      zIndex: 1,
                      filter: `blur(${channel.processed ? '0' : '3'}px)`,
                    }}
                    align="center"
                  >
                    {channel.alwaysPublishedMovies || 0}
                  </TableCell>
                  <TableCell
                    width={100}
                    sx={{
                      fontSize: '0.7rem',
                      lineHeight: '1.2rem',
                      zIndex: 1,
                      borderRight: '1px solid rgba(224, 224, 224, 1)',
                      filter: `blur(${channel.processed ? '0' : '3'}px)`,
                    }}
                    align="center"
                  >
                    {channel.alwaysPublishedEpisodes || 0}
                  </TableCell>
                  <TableCell
                    width={100}
                    sx={{
                      fontSize: '0.7rem',
                      lineHeight: '1.2rem',
                      zIndex: 1,
                      filter: `blur(${channel.processed ? '0' : '3'}px)`,
                    }}
                    align="center"
                  >
                    {channel.blockedVideos?.alwaysPublishedMovies || 0}
                  </TableCell>
                  <TableCell
                    width={100}
                    sx={{
                      fontSize: '0.7rem',
                      lineHeight: '1.2rem',
                      zIndex: 1,
                      borderRight: '1px solid rgba(224, 224, 224, 1)',
                      filter: `blur(${channel.processed ? '0' : '3'}px)`,
                    }}
                    align="center"
                  >
                    {channel.blockedVideos?.alwaysPublishedEpisodes || 0}
                  </TableCell>
                  <TableCell
                    width={100}
                    sx={{
                      fontSize: '0.7rem',
                      lineHeight: '1.2rem',
                      zIndex: 1,
                      filter: `blur(${channel.processed ? '0' : '3'}px)`,
                    }}
                    align="center"
                  >
                    {channel.blockedVideos?.totalMovies || 0}
                  </TableCell>
                  <TableCell
                    width={100}
                    sx={{
                      fontSize: '0.7rem',
                      lineHeight: '1.2rem',
                      zIndex: 1,
                      borderRight: '1px solid rgba(224, 224, 224, 1)',
                      filter: `blur(${channel.processed ? '0' : '3'}px)`,
                    }}
                    align="center"
                  >
                    {channel.blockedVideos?.totalEpisodes || 0}
                  </TableCell>
                  <TableCell
                    width={100}
                    sx={{
                      fontSize: '0.7rem',
                      lineHeight: '1.2rem',
                      zIndex: 1,
                      filter: `blur(${channel.processed ? '0' : '3'}px)`,
                    }}
                    align="center"
                  >
                    {channel.targetMovies || 0}
                  </TableCell>
                  <TableCell
                    width={100}
                    sx={{
                      fontSize: '0.7rem',
                      lineHeight: '1.2rem',
                      zIndex: 1,
                      borderRight: '1px solid rgba(224, 224, 224, 1)',
                      filter: `blur(${channel.processed ? '0' : '3'}px)`,
                    }}
                    align="center"
                  >
                    {channel.targetEpisodes || 0}
                  </TableCell>
                  <TableCell
                    width={100}
                    sx={{
                      fontSize: '0.7rem',
                      lineHeight: '1.2rem',
                      zIndex: 1,
                      filter: `blur(${channel.processed ? '0' : '3'}px)`,
                    }}
                    align="center"
                  >
                    {channel.weeklyScheduleSum || 0}
                  </TableCell>
                  {scheduleDates.map((date) => (
                    <React.Fragment
                      key={`row-t-${date}-${channel.id}-${idx}-data`}
                    >
                      <TableCell
                        sx={{
                          fontSize: '0.7rem',
                          lineHeight: '1.2rem',
                          zIndex: 1,
                          borderLeft: '1px solid rgba(224, 224, 224, 1)',
                          filter: `blur(${channel.processed ? '0' : '3'}px)`,
                        }}
                        align="center"
                      >
                        {channel[date]?.totalVideos || 0}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: '0.7rem',
                          lineHeight: '1.2rem',
                          zIndex: 1,
                          filter: `blur(${channel.processed ? '0' : '3'}px)`,
                        }}
                        align="center"
                      >
                        {channel[date]?.totalMovies || 0}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: '0.7rem',
                          lineHeight: '1.2rem',
                          zIndex: 1,
                          filter: `blur(${channel.processed ? '0' : '3'}px)`,
                        }}
                        align="center"
                      >
                        {channel[date]?.totalEpisodes || 0}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: '0.7rem',
                          lineHeight: '1.2rem',
                          zIndex: 1,
                          filter: `blur(${channel.processed ? '0' : '3'}px)`,
                        }}
                        align="center"
                      >
                        {channel[date]?.scheduledVideos || 0}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: '0.7rem',
                          lineHeight: '1.2rem',
                          zIndex: 1,
                          filter: `blur(${channel.processed ? '0' : '3'}px)`,
                        }}
                        align="center"
                      >
                        {channel[date]?.descheduledVideos || 0}
                      </TableCell>
                    </React.Fragment>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
    </Dialog>
  );
};

ValidateSetup.propTypes = {
  onCancel: PropTypes.func.isRequired,
};

export default ValidateSetup;
